import {
  AdvanceApproval,
  AdvanceStatus,
  Approval,
  ApprovalStatus,
  FinanceQueueRow,
  TravelRequestStatus
} from "../types";

/** Any approval-stage row that carries both the approval's OWN decision
 *  (`status`) and the underlying request/advance's real top-level status
 *  (`requestStatus`) - Approval, AdvanceApproval and FinanceQueueRow all
 *  have this shape. */
type ApprovalLikeRow = {
  status: ApprovalStatus;
  requestStatus?: TravelRequestStatus | AdvanceStatus;
};

/** `row.status` is the approval's OWN decision - it deliberately stays
 *  "Pending" after a Manager or Finance Approver sends the request/advance
 *  back for changes, so the employee's eventual resubmission routes
 *  straight back to the same approver (see db/approvals_store.py /
 *  db/advances_store.py's own _to_dict comments). That leaves a plain
 *  `row.status === "Pending"` check unable to tell a genuinely pending
 *  approval apart from one that's actually waiting on the EMPLOYEE to fix
 *  something (requestStatus "ResubmitRequested"/"Resubmit") - still
 *  offering Approve/Reject, or still counting toward "pending my approval",
 *  for something this approver can't currently act on. This is the one
 *  check for that, reused everywhere a row like this is rendered, counted
 *  or bucketed (MyApprovals.tsx, ApproverQueue.tsx's own displayStatus,
 *  Dashboard.tsx, DashboardVolumeChart.tsx). */
export function isActuallyPending(row: ApprovalLikeRow): boolean {
  return (
    row.status === "Pending" &&
    row.requestStatus !== "ResubmitRequested" &&
    row.requestStatus !== "Resubmit"
  );
}

/** The row's status as it should actually be DISPLAYED - "Pending" is
 *  replaced with the real "ResubmitRequested"/"Resubmit" when that's what's
 *  actually going on (see isActuallyPending above), otherwise the approval's
 *  own status is shown unchanged. */
export function approvalDisplayStatus(
  row: Approval | AdvanceApproval | FinanceQueueRow
): ApprovalStatus | "ResubmitRequested" | "Resubmit" {
  if (row.status !== "Pending" || isActuallyPending(row)) return row.status;
  return row.requestStatus as "ResubmitRequested" | "Resubmit";
}

/** Shared label for the one display status plain StatusBadge text doesn't
 *  already read naturally - both ResubmitRequested and Resubmit read the
 *  same to an approver ("I sent this back, it's on them now"). */
export const APPROVAL_DISPLAY_STATUS_LABEL: Record<string, string> = {
  ResubmitRequested: "Needs changes",
  Resubmit: "Needs changes"
};
